import { useNavigate } from "react-router";

import { AuthWrapper } from "@/lib/aws-amplify";
import { isAuthError } from "@/lib/aws-amplify/auth";

import { PATHS } from "@/routes/paths";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info/_hooks/use-toast";

import { useAuth } from "@/providers";

export const useSignInMfaOtp = () => {
	const { signInCallback } = useAuth();

	const navigate = useNavigate();

	const signInMfaOtp = async (code: string) =>
		await AuthWrapper.authConfirmSignInMfaOtp(code)
			.then(({ nextStep: { signInStep } }) => {
				/**
				 *
				 * @see file://./../_md/next-step/sign-in-step.md
				 */
				if (signInStep === "DONE") {
					// MFA を無効にしている場合
					notifyUserEventInfo({
						variant: "default",
						title: "サインインしました",
						description: "ようこそ！",
					});
					signInCallback();
				} else if (signInStep === "RESET_PASSWORD") {
					notifyUserEventInfo({
						variant: "default",
						title: "パスワードをリセットしてください。",
					});
					navigate(PATHS.AUTH.RESET_PASSWORD);
				} else if (signInStep === "CONFIRM_SIGN_UP") {
					notifyUserEventInfo({
						variant: "default",
						title: "認証が完了していません",
						description: "メールをご確認ください。",
					});
				} else if (signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE") {
					// MFA を有効にしている場合
					notifyUserEventInfo({
						variant: "default",
						title: "メールをご確認ください",
						description: "認証コードをログインメールアドレスに送信しました",
					});
					navigate(PATHS.AUTH.VERIFY_MFA_OTP);
				} else {
					/**
					 * @description
					 * 以下のケースは、認証フローとして存在しない。
					 * - CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED
					 * - CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE
					 * - CONFIRM_SIGN_IN_WITH_TOTP_CODE
					 * - CONTINUE_SIGN_IN_WITH_TOTP_SETUP
					 * - CONFIRM_SIGN_IN_WITH_SMS_CODE
					 * - CONTINUE_SIGN_IN_WITH_MFA_SELECTION
					 */
					notifyUserEventInfo({
						variant: "error",
						title: "予期せぬエラーが発生しました",
						description: "カスタマーサポートへお問い合わせください。",
					});
				}
			})
			.catch((err) => {
				const result = isAuthError(err);
				/**
				 *
				 * @description
				 * amplify/auth から throw されるエラーは以下、
				 * 1. InitiateAuthException
				 * 2. RespondToAuthChallengeException
				 * 3. AuthValidationErrorCode
				 * 4. AuthTokenConfigException
				 *
				 * 3,4 はクライアントでハンドリングしているため、1,2 のみ考慮する。
				 */
				const errorType = err?.name;
				if (result) {
					if (errorType === "NotAuthorizedException") {
						notifyUserEventInfo({
							variant: "error",
							title: "サインインに失敗しました",
							description: "メールアドレスかパスワードが間違っています。",
						});
					} else if (errorType === "CodeMismatchException") {
						notifyUserEventInfo({
							variant: "error",
							title: "サインインに失敗しました",
							description:
								"入力された認証コードが正しくありません。メールに記載のコードをご確認のうえ、再度入力してください。",
						});
					} else if (
						errorType === "TooManyFailedAttemptsException" ||
						errorType === "TooManyRequestsException" ||
						errorType === "LimitExceededException"
					) {
						notifyUserEventInfo({
							variant: "error",
							title: "ログインに失敗しました",
							description: "時間を空けて、もう一度お試しください。",
						});
					} else {
						notifyUserEventInfo({
							variant: "error",
							title: "サインインに失敗しました",
							description: "カスタマーサポートへお問い合わせください。",
						});
					}
				} else {
					notifyUserEventInfo({
						variant: "error",
						title: "サインインに失敗しました",
						description: "もう一度お試しください。",
					});
				}
			});

	return {
		signInMfaOtp,
	};
};
